import { useEffect, useState } from 'react';
import { api, type AdminQuestion } from '../../api/client';
import { useLocale } from '../../i18n/LocaleContext';
import { useAuth } from '../AuthContext';

type ListMode = 'status' | 'hottest' | 'never' | 'stems';

export function AdminPage() {
  const { user, ensureGuest, refresh } = useAuth();
  const { t } = useLocale();
  const [rows, setRows] = useState<AdminQuestion[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('draft');
  const [listMode, setListMode] = useState<ListMode>('status');
  const [stems, setStems] = useState<
    Array<{ stemKey: string; count: number; served: number; sample: string }>
  >([]);
  const [selected, setSelected] = useState<AdminQuestion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [secret, setSecret] = useState('');
  const [unlockMsg, setUnlockMsg] = useState<string | null>(null);
  const [editPrompt, setEditPrompt] = useState('');
  const [editChoices, setEditChoices] = useState(['', '', '', '']);
  const [editCorrect, setEditCorrect] = useState(0);
  const [editDiff, setEditDiff] = useState('medium');
  const [stemMsg, setStemMsg] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    setStemMsg(null);
    try {
      await ensureGuest();
      if (listMode === 'stems') {
        const res = await api.adminStems();
        setStems(res.rows);
        setRows([]);
        setTotal(res.rows.length);
        return;
      }
      const sort =
        listMode === 'hottest' ? 'hottest' : listMode === 'never' ? 'never' : 'created';
      const res = await api.adminQuestions({
        status: listMode === 'status' ? status : listMode === 'never' ? 'approved' : status || 'approved',
        sort,
        take: 50,
      });
      setRows(res.rows);
      setTotal(res.total);
      setStems([]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, listMode]);

  const open = async (id: string) => {
    const q = await api.adminQuestion(id);
    setSelected(q);
    setEditPrompt(q.prompt);
    setEditChoices([...(q.choices as string[])]);
    setEditCorrect(q.correctChoiceIndex);
    setEditDiff(q.difficulty);
  };

  const save = async () => {
    if (!selected) return;
    await api.adminPatchQuestion(selected.id, {
      prompt: editPrompt,
      choices: editChoices,
      correctChoiceIndex: editCorrect,
      difficulty: editDiff,
    });
    await load();
    await open(selected.id);
  };

  if (user && !user.isAdmin) {
    return (
      <section className="panel">
        <h1>{t('navAdmin')}</h1>
        <p className="muted">{t('adminAccess')}</p>
        <div className="field-row">
          <input
            type="password"
            placeholder="AUTH_SECRET"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
          />
          <button
            type="button"
            className="btn primary"
            onClick={() => {
              void api
                .devAdmin(secret)
                .then(() => refresh())
                .then(() => setUnlockMsg(t('settingsAdminEnabled')))
                .catch((e) => setUnlockMsg(e instanceof Error ? e.message : 'Failed'));
            }}
          >
            {t('settingsAdminUnlock')}
          </button>
        </div>
        {unlockMsg ? <p className="muted">{unlockMsg}</p> : null}
      </section>
    );
  }

  return (
    <section className="panel admin-panel">
      <h1>{t('adminTitle')}</h1>
      <p className="muted">{t('adminMatching', { total })}</p>
      {error ? <p className="error">{error}</p> : null}
      {stemMsg ? <p className="muted">{stemMsg}</p> : null}
      <div className="duration-row">
        {(
          [
            ['status', 'status'],
            ['hottest', t('adminHottest')],
            ['never', t('adminNeverServed')],
            ['stems', t('adminStems')],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={listMode === id ? 'btn primary' : 'btn'}
            onClick={() => setListMode(id)}
          >
            {label}
          </button>
        ))}
      </div>
      {listMode === 'status' ? (
        <div className="duration-row">
          {['draft', 'approved', 'rejected', ''].map((s) => (
            <button
              key={s || 'all'}
              type="button"
              className={status === s ? 'btn primary' : 'btn'}
              onClick={() => setStatus(s)}
            >
              {s || t('adminAll')}
            </button>
          ))}
          <button type="button" className="btn" onClick={() => void load()}>
            {t('adminRefresh')}
          </button>
        </div>
      ) : (
        <div className="duration-row">
          <button type="button" className="btn" onClick={() => void load()}>
            {t('adminRefresh')}
          </button>
        </div>
      )}
      <div className="admin-grid">
        {listMode === 'stems' ? (
          <ul className="admin-list">
            {stems.map((s) => (
              <li key={s.stemKey}>
                <div className="stem-row">
                  <strong>{s.stemKey}</strong>
                  <span className="muted">
                    {s.count} · {s.served} {t('adminServed')}
                  </span>
                  <span className="muted">{s.sample.slice(0, 72)}…</span>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      void api
                        .adminRejectStem(s.stemKey, 12)
                        .then((r) => {
                          setStemMsg(
                            `${r.stemKey}: rejected ${r.rejected}, kept ≤${r.kept} of ${r.matched}`,
                          );
                          return load();
                        })
                        .catch((e) =>
                          setError(e instanceof Error ? e.message : 'Stem reject failed'),
                        );
                    }}
                  >
                    {t('adminRejectStem')}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <ul className="admin-list">
            {rows.map((r) => (
              <li key={r.id}>
                <button type="button" className="linkish" onClick={() => void open(r.id)}>
                  <span className={`diff-pill diff-${r.difficulty}`}>{r.difficulty}</span>{' '}
                  {r.prompt.slice(0, 80)}
                  {r.prompt.length > 80 ? '…' : ''}
                  <span className="muted">
                    {' '}
                    · {r.status}
                    {r.servedCount != null ? ` · ${r.servedCount} ${t('adminServed')}` : ''}
                    {r.accuracy != null ? ` · ${r.accuracy}%` : ''}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {selected && listMode !== 'stems' ? (
          <div className="admin-detail">
            <label className="field">
              {t('adminPrompt')}
              <textarea value={editPrompt} onChange={(e) => setEditPrompt(e.target.value)} rows={3} />
            </label>
            {editChoices.map((c, i) => (
              <label key={i} className="field">
                {t('adminChoice')} {String.fromCharCode(65 + i)}
                <input
                  value={c}
                  onChange={(e) => {
                    const next = [...editChoices];
                    next[i] = e.target.value;
                    setEditChoices(next);
                  }}
                />
              </label>
            ))}
            <label className="field">
              {t('adminCorrect')}
              <select
                value={editCorrect}
                onChange={(e) => setEditCorrect(Number(e.target.value))}
              >
                {[0, 1, 2, 3].map((i) => (
                  <option key={i} value={i}>
                    {String.fromCharCode(65 + i)}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              {t('adminDifficulty')}
              <select value={editDiff} onChange={(e) => setEditDiff(e.target.value)}>
                <option value="easy">easy</option>
                <option value="medium">medium</option>
                <option value="hard">hard</option>
              </select>
            </label>
            <p className="muted">
              {t('adminStats')}: {selected.correctCount}/{selected.servedCount}{' '}
              {t('adminCorrectShort')} · {selected.wrongCount} {t('adminWrongShort')} ·{' '}
              {selected.timeoutCount} {t('adminTimeouts')}
            </p>
            <div className="cta-row">
              <button type="button" className="btn primary" onClick={() => void save()}>
                {t('adminSave')}
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => void api.adminApprove(selected.id).then(load)}
              >
                {t('adminApprove')}
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => void api.adminReject(selected.id).then(load)}
              >
                {t('adminReject')}
              </button>
            </div>
          </div>
        ) : listMode !== 'stems' ? (
          <p className="muted">{t('adminSelect')}</p>
        ) : null}
      </div>
    </section>
  );
}
